// InternLearnChaptered.js — Chaptered guide similar to LearnReact
import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  Link,
  Divider,
  Code,
  Button,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

const chapters = [
  {
    title: "1. Frontend Development (React + React Native)",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "React component lifecycle & hooks (useState, useEffect, useMemo, useCallback)",
          "React Native core components (View, Text, FlatList, ScrollView)",
          "Form handling with react-hook-form + yup schema validation",
          "Performance optimization with React.memo() and key props",
          "Expo Push Notifications setup and debugging",
          "State management (Context API / Zustand / Redux – basics only)",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "React Official Tutorial — https://react.dev/learn",
          "React Native Basics (Expo) — https://reactnative.dev/docs/environment-setup",
          "React Hook Form + Yup Guide — https://react-hook-form.com/get-started",
          "Expo Push Notifications — https://docs.expo.dev/push-notifications/overview/",
          "React.memo and Optimization — https://react.dev/reference/react/memo",
        ],
      },
      {
        title: "💡 Code Snippets & Clues",
        code: `// Proper state update
const [pickupDay, setPickupDay] = useState("Monday");
const handleSelect = (day) => setPickupDay(day);

// React.memo optimization
const OrderCard = React.memo(({ order }) => (
  <Text>{order.status}</Text>
));`,
      },
    ],
  },
  {
    title: "2. Backend Development (Spring Boot + REST APIs)",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "@RestController, @RequestMapping, @RequestBody, @PathVariable",
          "Status codes: 200 (OK), 201 (Created), 204 (No Content)",
          "Error handling (@ControllerAdvice, @ExceptionHandler)",
          "JWT authentication filter ordering (OncePerRequestFilter)",
          "Multipart file handling (spring.servlet.multipart.max-file-size)",
          "JSON communication between frontend and backend",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "Spring Boot Basics — https://spring.io/quickstart",
          "REST Controllers — https://spring.io/guides/gs/rest-service/",
          "JWT Authentication — https://www.baeldung.com/spring-security-jwt",
          "File Uploads — https://www.baeldung.com/spring-file-upload",
          "Exception Handling — https://www.baeldung.com/exception-handling-for-rest-with-spring",
        ],
      },
      {
        title: "💡 Code Snippet",
        code: `@PostMapping("/api/pickups")
public ResponseEntity<Map<String, Long>> createPickup(@RequestBody Pickup p) {
    Pickup saved = pickupRepo.save(p);
    return ResponseEntity.status(HttpStatus.CREATED)
            .body(Map.of("orderId", saved.getId()));
}`,
      },
    ],
  },
  {
    title: "3. Database (PostgreSQL)",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "Primary keys, foreign keys, indexes",
          "SQL joins, grouping, aggregation",
          "Unique constraints with conditions",
          "Query optimization using EXPLAIN",
          "Safe data deletion practices (transactions, scheduled jobs)",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "SQL Basics (PostgreSQL) — https://www.postgresqltutorial.com/",
          "Conditional Indexes — https://www.postgresql.org/docs/current/indexes-partial.html",
          "Foreign Keys & Joins — https://mode.com/sql-tutorial/sql-joins",
        ],
      },
      {
        title: "💡 Code Snippet",
        code: `-- Fast filter for pending orders
CREATE INDEX idx_pickups_status ON pickups(status);

-- Enforce one active subscription
CREATE UNIQUE INDEX uniq_active_sub ON subscriptions(customer_id)
WHERE status='ACTIVE';`,
      },
    ],
  },
  {
    title: "4. Debugging & Integration Testing",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "Postman API testing (headers, payload)",
          "Common HTTP status codes (400, 401, 403, 415)",
          "JSON schema mismatch issues",
          "Emulator vs. localhost networking (10.0.2.2 in Android)",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "Postman Basics — https://learning.postman.com/docs/getting-started/",
          "Android Emulator Networking — https://developer.android.com/studio/run/emulator-networking",
        ],
      },
      { text: "Always match frontend JSON keys with backend DTO field names." },
    ],
  },
  {
    title: "5. Security & Payments",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "JWT-based authentication and refresh tokens",
          "PCI DSS basics for handling card data",
          "Stripe SDK for React Native and Spring Boot",
          "Webhooks and asynchronous events",
          "Secure environment variables & .gitignore",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "JWT Intro — https://jwt.io/introduction/",
          "Stripe Payments (React Native) — https://stripe.com/docs/payments/accept-a-payment?platform=react-native",
          "Spring Boot Webhooks — https://stripe.com/docs/webhooks/spring",
          "Secure Env Management — https://12factor.net/config",
        ],
      },
      {
        title: "💡 Code Snippet",
        code: `@PostMapping("/stripe/webhook")
public ResponseEntity<String> handleWebhook(@RequestBody String payload,
   @RequestHeader("Stripe-Signature") String sigHeader) {
    // verify and process event
    return ResponseEntity.ok("Received");
}`,
      },
    ],
  },
  {
    title: "6. DevOps & CI/CD",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "Node.js version alignment between local & Netlify",
          "Render/Heroku memory allocation & scaling",
          "GitHub Actions basics for CI",
          ".env secrets & variable injection",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "Netlify CI/CD Docs — https://docs.netlify.com/configure-builds/",
          "Render Deploy Spring Boot — https://render.com/docs/deploy-spring-boot",
          "GitHub Actions for Java — https://docs.github.com/en/actions",
        ],
      },
    ],
  },
  {
    title: "7. Architecture & Design Patterns",
    descriptionSections: [
      {
        title: "🎯 Key Concepts",
        points: [
          "DTO pattern & data exposure control",
          "Saga pattern for compensating transactions",
          "Circuit Breaker pattern in distributed systems",
          "Layered architecture (Controller → Service → Repository)",
        ],
      },
      {
        title: "📘 Learn Online",
        points: [
          "DTO Pattern in Spring — https://www.baeldung.com/entity-to-and-from-dto-for-a-java-spring-application",
          "Saga Pattern Explained — https://microservices.io/patterns/data/saga.html",
          "Circuit Breaker (Resilience4j) — https://resilience4j.readme.io/docs/circuitbreaker",
        ],
      },
      {
        title: "💡 Visual Summary",
        code: `[Client] → [Controller] → [Service Layer] → [Repository] → [Database]\n                   ↓                     ↓\n              DTO Conversion         Transaction Handling (Saga)`,
      },
    ],
  },
  {
    title: "Summary & Study Plan",
    descriptionSections: [
      {
        title: "📚 Summary Checklist for Interns",
        points: [
          "Frontend — Hooks, state, props, performance",
          "Backend — REST endpoints, HTTP verbs, JWT",
          "Database — Indexes, constraints, safe deletes",
          "Integration — JSON consistency, Postman testing",
          "Security — Stripe, Twilio, JWT, Env secrets",
          "DevOps — Build configs, Render, Netlify",
          "Design — DTOs, Saga, Microservice basics",
        ],
      },
      {
        title: "💡 How to Study (3 Days)",
        points: [
          "Day 1: Frontend + API fundamentals",
          "Day 2: Backend + Database + Security",
          "Day 3: CI/CD + Patterns + Mock Q&A review",
          "Build a small feature (e.g., Schedule Pickup) and test via Postman + SQLite/Postgres.",
        ],
      },
    ],
  },
];

const InternLearnChaptered = () => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentChapter < chapters.length - 1) setCurrentChapter(currentChapter + 1);
  };
  const handlePrevious = () => {
    if (currentChapter > 0) setCurrentChapter(currentChapter - 1);
  };
  const handleStartTest = () => navigate("/intern-test?start=true");

  const current = chapters[currentChapter];

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="2xl" textAlign="center">
          Intern Learn — Prep Guide
        </Heading>
        <Text fontSize="lg" textAlign="center" color="gray.600">
          Structured chapters with links, snippets, and a 3-day plan.
        </Text>

        <Divider />

        <Box p={6} shadow="md" borderWidth="1px" borderRadius="md" minH="520px" bg="white">
          <Heading as="h2" size="lg" mb={3}>
            {current.title}
          </Heading>

          {current.descriptionSections.map((section, idx) => (
            <Box key={idx} mb={4}>
              {section.title && (
                <Text fontWeight="bold" fontSize="md" mb={2}>
                  {section.title}
                </Text>
              )}
              {section.text && <Text mb={2}>{section.text}</Text>}
              {section.points && (
                <List spacing={2} pl={4} styleType="disc">
                  {section.points.map((pt, i) => (
                    <ListItem key={i}>
                      {pt.includes("http") ? (
                        <Link href={pt.split(" — ")[1]} color="teal.500" isExternal>
                          {pt}
                        </Link>
                      ) : (
                        pt
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
              {section.code && (
                <Box bg="gray.800" p={4} borderRadius="md" overflowX="auto" mt={2}>
                  <Code colorScheme="cyan" whiteSpace="pre-wrap" display="block">
                    {section.code}
                  </Code>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <HStack justifyContent="space-between">
          <Button onClick={handlePrevious} isDisabled={currentChapter === 0} variant="outline" colorScheme="teal">
            Previous
          </Button>

          <Text>Chapter {currentChapter + 1} of {chapters.length}</Text>

          {currentChapter === chapters.length - 1 ? (
            <Button colorScheme="orange" onClick={handleStartTest}>Start Intern Test</Button>
          ) : (
            <Button onClick={handleNext} colorScheme="teal">Next</Button>
          )}
        </HStack>
      </VStack>
    </Container>
  );
};

export default InternLearnChaptered;
